import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared/src/propertyGrid/consts';
import { Grid, ProductColor } from '@aloft/toolkit-sui';

import { useEffect, useMemo, useRef } from 'react';
import { shallowEqual } from 'react-redux';

import { useZodForm } from '../../hooks/useZodForm.ts';
import {
  selectCompSequence,
  selectHasArvOption,
} from '../../redux/features/selectors/orderSelectors.ts';
import {
  selectPropertyDataCompTypes,
} from '../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import {
  selectAivArvActiveTab,
  selectFeatureGridExpandedPropertyId,
} from '../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { PropertyGridColumn } from './PropertyGridColumn.tsx';
import {
  AIV_ARV_PROPERTY_GRID_COLUMN_WIDTH,
  FEATURE_GRID_DATA_SOURCES,
  PROPERTY_GRID_COLUMN_WIDTH,
} from './consts.ts';
import {
  ColumnType,
  PropertyGridColumnSectionComponent,
  PropertyGridRowFieldType,
  PropertyGridType,
  schPropertyGridForm,
} from './types.ts';
import { getDefaultFormColumns } from './utils.ts';

type PropertyGridProps = {
  columnSections: PropertyGridColumnSectionComponent[];
  gridType: PropertyGridType;
};

export const PropertyGrid = ({ columnSections, gridType }: PropertyGridProps) => {
  const compSequence = useAppSelector(selectCompSequence);
  const featureGridExpandedPropertyId = useAppSelector(selectFeatureGridExpandedPropertyId);
  const prevCompSequenceRef = useRef<string[]>(compSequence);
  const hasArvOption = useAppSelector(selectHasArvOption);
  const activeTab = useAppSelector(selectAivArvActiveTab);
  const propertyDataCompTypes = useAppSelector(selectPropertyDataCompTypes, shallowEqual);

  const isFeatureGrid = gridType === 'FeatureGrid';

  const zodForm = useZodForm({
    schema: schPropertyGridForm,
    defaultValues: schPropertyGridForm.parse({
      columns: getDefaultFormColumns(compSequence, FEATURE_GRID_DATA_SOURCES),
      dataSources: FEATURE_GRID_DATA_SOURCES,
    }),
    mode: 'onBlur',
  });

  const { control, getValues, setValue } = zodForm;

  const columns = useMemo(
    () => getDefaultFormColumns(compSequence, FEATURE_GRID_DATA_SOURCES),
    [compSequence],
  );

  // Update form columns only when new comps are added (not when sequence changes)
  useEffect(() => {
    const prevCompSequence = prevCompSequenceRef.current;
    const newComps = compSequence.filter((compId) => !prevCompSequence.includes(compId));
    const currentColumns = getValues('columns') || [];

    const deletedComps = prevCompSequence.filter((compId) => !compSequence.includes(compId));
    if (deletedComps.length > 0) {
      // Remove columns for deleted comps
      const columnsToRemove = currentColumns.filter((col) =>
        deletedComps.some((deletedCompId) => deletedCompId === col.propertyId),
      );
      setValue(
        'columns',
        currentColumns.filter(
          (col) =>
            !columnsToRemove.some((colToRemove) => colToRemove.propertyId === col.propertyId),
        ),
      );
    }

    // This is a bit messy, but it works
    // Reordering columns seems to move property IDs around but not their underlying data
    // So we need to find the correct property ID for each column and update it
    if (newComps.length === 0 && deletedComps.length === 0) {
      const newCols = [...currentColumns];
      const numberOfColumnsForSubject = currentColumns.filter(
        (col) => col.propertyId === PROPERTY_GRID_SUBJECT_PROPERTY,
      ).length;
      currentColumns.forEach((col, index) => {
        if (!col.propertyData || index < numberOfColumnsForSubject) {
          return;
        }
        const numberOfColumnsPerProperty = currentColumns.filter(
          (otherCol) => otherCol.propertyId === col.propertyId,
        ).length;
        if (col.propertyId !== compSequence[Math.floor(index / numberOfColumnsPerProperty) - 1]) {
          const correctPropertyData =
            currentColumns[
            numberOfColumnsForSubject +
            numberOfColumnsPerProperty * compSequence.indexOf(col.propertyId) +
            (index % numberOfColumnsPerProperty)
            ];
          if (!correctPropertyData) {
            return;
          }
          newCols[index] = {
            ...col,
            propertyId: correctPropertyData.propertyId,
          };
        }
      });

      setValue('columns', newCols);
    }

    prevCompSequenceRef.current = compSequence;
  }, [compSequence, getValues, setValue]);

  const orderedColumns = useMemo(() => {
    const subjectCols = columns.filter(
      ({ propertyId }) => propertyId === PROPERTY_GRID_SUBJECT_PROPERTY,
    );

    const filteredCompSequence =
      (isFeatureGrid || gridType === 'AdjustmentsGrid') && hasArvOption
        ? compSequence.filter((compId) => {
          if (activeTab === 'all') return true;
          const compType = propertyDataCompTypes[compId];
          if (!compType) return false;
          return compType === activeTab;
        })
        : compSequence;

    const compCols = filteredCompSequence.flatMap((compId) => {
      const existingCol = columns.filter(({ propertyId }) => propertyId === compId);
      if (existingCol.length > 0) return existingCol;

      return [
        {
          propertyId: compId,
          type: 'property' as ColumnType,
        },
        ...FEATURE_GRID_DATA_SOURCES.map((source) => ({
          propertyId: compId,
          type: source,
        })),
      ];
    });

    return [...subjectCols, ...compCols];
  }, [
    compSequence,
    columns,
    isFeatureGrid,
    gridType,
    hasArvOption,
    activeTab,
    propertyDataCompTypes,
  ]);

  const visibleColumns = orderedColumns.filter(
    ({ type, propertyId }) =>
      type === 'property' || (isFeatureGrid && featureGridExpandedPropertyId === propertyId),
  );

  const isDataSourceDrawerOpen =
    isFeatureGrid &&
    visibleColumns.some(({ propertyId }) => featureGridExpandedPropertyId === propertyId);

  let backgroundColor: ProductColor = 'surface6';
  if (activeTab === 'aiv') backgroundColor = 'aivLight';
  else if (activeTab === 'arv') backgroundColor = 'arvLight';

  return (
    <Grid
      backgroundColor={backgroundColor}
      gridTemplateColumns={`${hasArvOption && activeTab === 'all' ? AIV_ARV_PROPERTY_GRID_COLUMN_WIDTH : PROPERTY_GRID_COLUMN_WIDTH} repeat(${visibleColumns.length}, ${PROPERTY_GRID_COLUMN_WIDTH})`}
      paddingLeft="10px"
      paddingRight="10px"
      paddingBottom="5rem"
      minWidth="max-content"
      width="100%"
    >
      {orderedColumns.map((col) => {
        const { propertyId, type } = col;
        const isSubjectProperty = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;
        const propertyIsExpanded = isFeatureGrid && featureGridExpandedPropertyId === propertyId;

        const columnIndex = columns.findIndex(
          (column) => column.propertyId === propertyId && column.type === type,
        );

        const showFieldLabels = type === 'property' && (isSubjectProperty || propertyIsExpanded);
        const fieldTypes: PropertyGridRowFieldType[] = [];

        if (showFieldLabels) fieldTypes.push('label');
        if (isFeatureGrid) {
          if (type === 'property') {
            fieldTypes.push('editableValue');
          } else {
            fieldTypes.push('dataSourceValue');
          }
        } else if (gridType === 'AdjustmentsGrid') {
          // For subject column in AdjustmentsGrid, filter based on activeTab
          if (isSubjectProperty && hasArvOption) {
            const showAivValue = activeTab === 'all' || activeTab === 'aiv';
            const showArvValue = activeTab === 'all' || activeTab === 'arv';
            if (showAivValue) fieldTypes.push('readOnlyValue');
            if (showArvValue) fieldTypes.push('readOnlyArvValue');
          } else {
            fieldTypes.push('readOnlyValue');
          }
          if (!showFieldLabels) fieldTypes.push('adjustment');
        }
        if (hasArvOption && isFeatureGrid) {
          fieldTypes.push('editableArvValue');
        }

        return (
          <PropertyGridColumn
            key={`${propertyId}-${type}`}
            fieldTypes={fieldTypes}
            columnSections={columnSections}
            gridType={gridType}
            isDataSourceDrawerOpen={isDataSourceDrawerOpen}
            propertyId={propertyId}
            type={type}
            columnIndex={columnIndex}
            control={control}
            getValues={getValues}
            setValue={setValue}
          />
        );
      })}
    </Grid>
  );
};
